import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { lldProgressService, SaveProgressRequest } from '../services/lldProgressService';
import {
  Mode,
  SectionId,
  SectionStatus,
  getSectionStates,
  SectionProgress,
} from '../utils/sectionStateMachine';
import { calculateActiveTime } from '../utils/activeTimeCalculator';
import { recordVisit, VisitEntry } from '../utils/visitHistory';

// ─── Types ──────────────────────────────────────────────────────────────────────

export type Language = 'typescript' | 'javascript' | 'python';
type RelationshipType = 'association' | 'aggregation' | 'composition' | 'inheritance' | 'dependency';
type Cardinality = '1-to-1' | '1-to-many' | 'many-to-one' | 'many-to-many';
type AccessModifier = 'public' | 'private' | 'protected';
type LayoutPreset = 'code-focus' | 'design-focus' | 'balanced' | 'custom';

export interface Entity {
  id: string;
  name: string;
  description: string;
  createdAt: number;
}

const normalizeRestoredEntities = (value: unknown): Entity[] => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    .map((item, index) => {
      const legacyName = item.name ?? item.entityName ?? item.title ?? item.label;
      const name = typeof legacyName === 'string' && legacyName.trim()
        ? legacyName.trim()
        : `Untitled Entity ${index + 1}`;
      return {
        id: typeof item.id === 'string' && item.id ? item.id : `restored-entity-${index}-${Date.now()}`,
        name,
        description: typeof item.description === 'string' ? item.description : '',
        createdAt: typeof item.createdAt === 'number' ? item.createdAt : Date.now(),
      };
    });
};

export interface Attribute {
  id: string;
  name: string;
  type: string;
  accessModifier: AccessModifier;
  categoryLabel?: string;
  order: number;
}

export interface Parameter {
  name: string;
  type: string;
}

export interface Method {
  id: string;
  name: string;
  parameters: Parameter[];
  returnType: string;
  accessModifier: AccessModifier;
  categoryLabel?: string;
  order: number;
}

export interface EntityResponsibility {
  entityId: string;
  attributes: Attribute[];
  methods: Method[];
}

export interface Relationship {
  id: string;
  sourceEntityId: string;
  targetEntityId: string;
  type: RelationshipType;
  cardinality: Cardinality;
  label?: string;
}

export interface UMLElement {
  id: string;
  type: 'class' | 'interface' | 'abstract-class';
  name: string;
  x: number;
  y: number;
  fields: { access: '+' | '-' | '#'; name: string; type: string }[];
  methods: { access: '+' | '-' | '#'; signature: string }[];
}

export interface UMLConnection {
  id: string;
  fromId: string;
  toId: string;
  type: RelationshipType;
  cardinality: Cardinality;
}

export interface UMLDiagramData {
  elements: UMLElement[];
  connections: UMLConnection[];
}

export interface FileNode {
  path: string;
  content: string;
}

export interface TestResult {
  id: string;
  name: string;
  category: 'functional' | 'edge-case';
  status: 'passed' | 'failed' | 'skipped';
  assertion?: string;
  expected?: string;
  actual?: string;
}

export type RequirementCategory = 'functional' | 'non-functional' | 'assumption';

export interface RequirementItem {
  id: string;
  text: string;
  category: RequirementCategory;
}

// ─── State Interfaces ───────────────────────────────────────────────────────────

export interface ProblemState {
  problemId: string | null;
  title: string;
  description: string;
  hints: Record<string, string[]>;
  referenceSolution: object | null;
}

export interface WorkspaceState {
  entities: Entity[];
  responsibilities: Record<string, EntityResponsibility>;
  relationships: Relationship[];
  umlDiagram: UMLDiagramData;
  codeFiles: Record<Language, FileNode[]>;
  activeLanguage: Language;
  testResults: TestResult[] | null;
  requirements: RequirementItem[];
  // Editable problem statement for scratch/blank sessions (session-local).
  brief: { title: string; description: string };
}

export interface SessionState {
  mode: Mode;
  currentSection: SectionId;
  sectionProgress: SectionProgress;
  sectionStates: Record<SectionId, SectionStatus>;
  visitHistory: VisitEntry[];
  timeSpentPerSection: Record<SectionId, number>;
  timer: { totalSeconds: number; remainingSeconds: number; isRunning: boolean } | null;
  hintsRevealed: Record<string, number>;
  solutionsRevealed: string[];
  layoutPreset: LayoutPreset;
  panelVisibility: Record<string, boolean>;
  panelSizes: Record<string, number>;
}

export interface LLDPracticeState {
  problemState: ProblemState;
  workspaceState: WorkspaceState;
  sessionState: SessionState;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt: number | null;
  activityTimestamps: number[];
}

// ─── Actions ────────────────────────────────────────────────────────────────────

type LLDPracticeAction =
  | { type: 'SET_PROBLEM'; payload: { problemId: string; title: string; description: string; hints: Record<string, string[]>; referenceSolution: object | null } }
  | { type: 'SET_MODE'; payload: { mode: Mode; interviewSeconds?: number; interviewRemainingSeconds?: number } }
  | { type: 'ADD_ENTITY'; payload: Entity }
  | { type: 'REMOVE_ENTITY'; payload: string }
  | { type: 'UPDATE_ENTITY'; payload: { id: string; updates: Partial<Omit<Entity, 'id'>> } }
  | { type: 'ADD_ATTRIBUTE'; payload: { entityId: string; attribute: Attribute } }
  | { type: 'ADD_METHOD'; payload: { entityId: string; method: Method } }
  | { type: 'ADD_RELATIONSHIP'; payload: Relationship }
  | { type: 'REMOVE_RELATIONSHIP'; payload: string }
  | { type: 'ADD_REQUIREMENT'; payload: RequirementItem }
  | { type: 'REMOVE_REQUIREMENT'; payload: string }
  | { type: 'SET_BRIEF'; payload: { title?: string; description?: string } }
  | { type: 'UPDATE_UML'; payload: UMLDiagramData }
  | { type: 'UPDATE_CODE'; payload: { language: Language; files: FileNode[] } }
  | { type: 'SET_SECTION'; payload: SectionId }
  | { type: 'COMPLETE_SECTION'; payload: SectionId }
  | { type: 'REVEAL_HINT'; payload: string }
  | { type: 'REVEAL_SOLUTION'; payload: string }
  | { type: 'TICK_TIMER' }
  | { type: 'TOGGLE_TIMER' }
  | { type: 'RESET_TIMER' }
  | { type: 'SET_LAYOUT'; payload: LayoutPreset }
  | { type: 'TOGGLE_PANEL'; payload: string }
  | { type: 'SAVE_SUCCESS'; payload: number }
  | { type: 'SAVE_FAILURE' }
  | { type: 'SET_TEST_RESULTS'; payload: TestResult[] }
  | { type: 'RESET_WORKSPACE' }
  | { type: 'RECORD_ACTIVITY'; payload: number }
  | { type: 'RESTORE_STATE'; payload: Partial<LLDPracticeState> };

// ─── Initial State ──────────────────────────────────────────────────────────────

const DEFAULT_SECTION_PROGRESS: SectionProgress = {
  problem: false,
  requirements: false,
  entities: false,
  responsibilities: false,
  relationships: false,
  uml: false,
  implementation: false,
  testing: false,
};

const initialState: LLDPracticeState = {
  problemState: {
    problemId: null,
    title: '',
    description: '',
    hints: {},
    referenceSolution: null,
  },
  workspaceState: {
    entities: [],
    responsibilities: {},
    relationships: [],
    umlDiagram: { elements: [], connections: [] },
    codeFiles: { typescript: [], javascript: [], python: [] },
    activeLanguage: 'typescript',
    testResults: null,
    requirements: [],
    brief: { title: '', description: '' },
  },
  sessionState: {
    mode: 'practice',
    currentSection: 'problem',
    sectionProgress: { ...DEFAULT_SECTION_PROGRESS },
    sectionStates: getSectionStates('practice', DEFAULT_SECTION_PROGRESS),
    visitHistory: [],
    timeSpentPerSection: {
      problem: 0,
      requirements: 0,
      entities: 0,
      responsibilities: 0,
      relationships: 0,
      uml: 0,
      implementation: 0,
      testing: 0,
    },
    timer: null,
    hintsRevealed: {},
    solutionsRevealed: [],
    layoutPreset: 'balanced',
    panelVisibility: {},
    panelSizes: {},
  },
  saveStatus: 'idle',
  lastSavedAt: null,
  activityTimestamps: [],
};

// ─── Reducer ────────────────────────────────────────────────────────────────────

function lldPracticeReducer(state: LLDPracticeState, action: LLDPracticeAction): LLDPracticeState {
  switch (action.type) {
    case 'SET_PROBLEM':
      return {
        ...state,
        problemState: {
          problemId: action.payload.problemId,
          title: action.payload.title,
          description: action.payload.description,
          hints: action.payload.hints,
          referenceSolution: action.payload.referenceSolution,
        },
      };

    case 'SET_MODE': {
      const { mode: newMode, interviewSeconds, interviewRemainingSeconds } = action.payload;
      const sectionStates = getSectionStates(newMode, state.sessionState.sectionProgress);
      const total = interviewSeconds && interviewSeconds > 0 ? interviewSeconds : 2700;
      const remaining =
        typeof interviewRemainingSeconds === 'number' && interviewRemainingSeconds >= 0
          ? Math.min(interviewRemainingSeconds, total)
          : total;
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          mode: newMode,
          sectionStates,
          // Interview timer starts PAUSED — the candidate begins it with Play.
          timer: newMode === 'interview'
            ? { totalSeconds: total, remainingSeconds: remaining, isRunning: false }
            : null,
        },
      };
    }

    case 'ADD_ENTITY':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          entities: [...state.workspaceState.entities, action.payload],
        },
      };

    case 'REMOVE_ENTITY': {
      const entityId = action.payload;
      const newResponsibilities = { ...state.workspaceState.responsibilities };
      delete newResponsibilities[entityId];
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          entities: state.workspaceState.entities.filter((e) => e.id !== entityId),
          responsibilities: newResponsibilities,
          relationships: state.workspaceState.relationships.filter(
            (r) => r.sourceEntityId !== entityId && r.targetEntityId !== entityId
          ),
        },
      };
    }

    case 'UPDATE_ENTITY':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          entities: state.workspaceState.entities.map((e) =>
            e.id === action.payload.id ? { ...e, ...action.payload.updates } : e
          ),
        },
      };

    case 'ADD_ATTRIBUTE': {
      const { entityId, attribute } = action.payload;
      const existing = state.workspaceState.responsibilities[entityId] || {
        entityId,
        attributes: [],
        methods: [],
      };
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          responsibilities: {
            ...state.workspaceState.responsibilities,
            [entityId]: {
              ...existing,
              attributes: [...existing.attributes, attribute],
            },
          },
        },
      };
    }

    case 'ADD_METHOD': {
      const { entityId, method } = action.payload;
      const existing = state.workspaceState.responsibilities[entityId] || {
        entityId,
        attributes: [],
        methods: [],
      };
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          responsibilities: {
            ...state.workspaceState.responsibilities,
            [entityId]: {
              ...existing,
              methods: [...existing.methods, method],
            },
          },
        },
      };
    }

    case 'ADD_RELATIONSHIP':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          relationships: [...state.workspaceState.relationships, action.payload],
        },
      };

    case 'REMOVE_RELATIONSHIP':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          relationships: state.workspaceState.relationships.filter(
            (r) => r.id !== action.payload
          ),
        },
      };

    case 'ADD_REQUIREMENT':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          requirements: [...state.workspaceState.requirements, action.payload],
        },
      };

    case 'REMOVE_REQUIREMENT':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          requirements: state.workspaceState.requirements.filter(
            (r) => r.id !== action.payload
          ),
        },
      };

    case 'SET_BRIEF':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          brief: {
            title: action.payload.title ?? state.workspaceState.brief.title,
            description: action.payload.description ?? state.workspaceState.brief.description,
          },
        },
      };

    case 'UPDATE_UML':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          umlDiagram: action.payload,
        },
      };

    case 'UPDATE_CODE':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          codeFiles: {
            ...state.workspaceState.codeFiles,
            [action.payload.language]: action.payload.files,
          },
          activeLanguage: action.payload.language,
        },
      };

    case 'SET_SECTION': {
      const requestedSection = action.payload === 'testing' ? 'implementation' : action.payload;
      const newVisitHistory = recordVisit(
        state.sessionState.visitHistory,
        requestedSection,
        Date.now()
      );
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          currentSection: requestedSection,
          visitHistory: newVisitHistory,
        },
      };
    }

    case 'COMPLETE_SECTION': {
      const newProgress: SectionProgress = {
        ...state.sessionState.sectionProgress,
        [action.payload]: true,
      };
      const sectionStates = getSectionStates(state.sessionState.mode, newProgress);
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          sectionProgress: newProgress,
          sectionStates,
        },
      };
    }

    case 'REVEAL_HINT': {
      const section = action.payload;
      const currentCount = state.sessionState.hintsRevealed[section] || 0;
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          hintsRevealed: {
            ...state.sessionState.hintsRevealed,
            [section]: currentCount + 1,
          },
        },
      };
    }

    case 'REVEAL_SOLUTION':
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          solutionsRevealed: state.sessionState.solutionsRevealed.includes(action.payload)
            ? state.sessionState.solutionsRevealed
            : [...state.sessionState.solutionsRevealed, action.payload],
        },
      };

    case 'TICK_TIMER': {
      if (!state.sessionState.timer || !state.sessionState.timer.isRunning) {
        return state;
      }
      const remaining = state.sessionState.timer.remainingSeconds - 1;
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          timer: {
            ...state.sessionState.timer,
            remainingSeconds: Math.max(0, remaining),
            isRunning: remaining > 0,
          },
        },
      };
    }

    case 'TOGGLE_TIMER': {
      // Pause/resume the countdown. Can't resume a timer that's already at 0.
      if (!state.sessionState.timer) return state;
      const { remainingSeconds, isRunning } = state.sessionState.timer;
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          timer: {
            ...state.sessionState.timer,
            isRunning: isRunning ? false : remainingSeconds > 0,
          },
        },
      };
    }

    case 'RESET_TIMER': {
      // Reset the countdown back to the full configured duration, PAUSED —
      // the candidate restarts it explicitly with the Start button.
      if (!state.sessionState.timer) return state;
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          timer: {
            ...state.sessionState.timer,
            remainingSeconds: state.sessionState.timer.totalSeconds,
            isRunning: false,
          },
        },
      };
    }

    case 'SET_LAYOUT':
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          layoutPreset: action.payload,
        },
      };

    case 'TOGGLE_PANEL': {
      const panelId = action.payload;
      const currentVisibility = state.sessionState.panelVisibility[panelId] !== false;
      return {
        ...state,
        sessionState: {
          ...state.sessionState,
          panelVisibility: {
            ...state.sessionState.panelVisibility,
            [panelId]: !currentVisibility,
          },
        },
      };
    }

    case 'SAVE_SUCCESS':
      return {
        ...state,
        saveStatus: 'saved',
        lastSavedAt: action.payload,
      };

    case 'SAVE_FAILURE':
      return {
        ...state,
        saveStatus: 'error',
      };

    case 'SET_TEST_RESULTS':
      return {
        ...state,
        workspaceState: {
          ...state.workspaceState,
          testResults: action.payload,
        },
      };

    case 'RESET_WORKSPACE':
      return {
        ...initialState,
        problemState: state.problemState,
        sessionState: {
          ...initialState.sessionState,
          mode: state.sessionState.mode,
          sectionStates: getSectionStates(state.sessionState.mode, DEFAULT_SECTION_PROGRESS),
        },
      };

    case 'RECORD_ACTIVITY':
      return {
        ...state,
        activityTimestamps: [...state.activityTimestamps, action.payload],
      };

    case 'RESTORE_STATE': {
      const restored = action.payload;
      return {
        ...state,
        ...restored,
        problemState: restored.problemState || state.problemState,
        workspaceState: restored.workspaceState
          ? {
            ...restored.workspaceState,
            entities: normalizeRestoredEntities(restored.workspaceState.entities),
            brief: restored.workspaceState.brief || state.workspaceState.brief || { title: '', description: '' },
          }
          : state.workspaceState,
        sessionState: restored.sessionState
          ? {
            ...state.sessionState,
            ...restored.sessionState,
            currentSection: restored.sessionState.currentSection === 'testing'
              ? 'implementation'
              : restored.sessionState.currentSection || state.sessionState.currentSection,
            sectionStates: getSectionStates(
              restored.sessionState.mode || state.sessionState.mode,
              restored.sessionState.sectionProgress || state.sessionState.sectionProgress
            ),
          }
          : state.sessionState,
      };
    }

    default:
      return state;
  }
}

// ─── Context Type ───────────────────────────────────────────────────────────────

interface LLDPracticeContextType {
  state: LLDPracticeState;
  dispatch: React.Dispatch<LLDPracticeAction>;
  getActiveTime: () => number;
}

// ─── Context ────────────────────────────────────────────────────────────────────

const LLDPracticeContext = createContext<LLDPracticeContextType | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────────

interface LLDPracticeProviderProps {
  children: ReactNode;
  problemId?: string;
}

export const LLDPracticeProvider: React.FC<LLDPracticeProviderProps> = ({ children, problemId }) => {
  const [state, dispatch] = useReducer(lldPracticeReducer, initialState);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const workspaceStateRef = useRef(state.workspaceState);
  const sessionStateRef = useRef(state.sessionState);

  // Keep refs in sync with state
  useEffect(() => {
    workspaceStateRef.current = state.workspaceState;
  }, [state.workspaceState]);

  useEffect(() => {
    sessionStateRef.current = state.sessionState;
  }, [state.sessionState]);

  // ─── Auto-save with 5-second debounce ───────────────────────────────────────

  useEffect(() => {
    // Don't auto-save if no problem is loaded
    if (!state.problemState.problemId) {
      return;
    }

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set a new 5-second debounce timer
    saveTimeoutRef.current = setTimeout(async () => {
      const currentProblemId = state.problemState.problemId;
      if (!currentProblemId) return;

      const saveData: SaveProgressRequest = {
        mode: sessionStateRef.current.mode,
        entities: workspaceStateRef.current.entities,
        responsibilities: workspaceStateRef.current.responsibilities,
        relationships: workspaceStateRef.current.relationships,
        umlDiagram: workspaceStateRef.current.umlDiagram,
        codeFiles: workspaceStateRef.current.codeFiles,
        activeLanguage: workspaceStateRef.current.activeLanguage,
        requirements: workspaceStateRef.current.requirements,
        brief: workspaceStateRef.current.brief,
        sectionStatus: Object.fromEntries(
          Object.entries(sessionStateRef.current.sectionStates)
        ),
        sectionVisitHistory: sessionStateRef.current.visitHistory.map((v) => ({
          section: v.section,
          timestamp: v.timestamp,
        })),
        timeSpentPerSection: sessionStateRef.current.timeSpentPerSection,
        totalTimeSpent: calculateActiveTime(state.activityTimestamps),
        timerTotalSeconds: sessionStateRef.current.timer?.totalSeconds,
        timerRemainingSeconds: sessionStateRef.current.timer?.remainingSeconds,
        hintsRevealed: sessionStateRef.current.hintsRevealed,
        solutionsRevealed: sessionStateRef.current.solutionsRevealed,
        layoutPreset: sessionStateRef.current.layoutPreset,
        panelVisibility: sessionStateRef.current.panelVisibility,
        panelSizes: sessionStateRef.current.panelSizes,
      };

      try {
        await lldProgressService.saveProgress(currentProblemId, saveData);
        dispatch({ type: 'SAVE_SUCCESS', payload: Date.now() });
      } catch {
        dispatch({ type: 'SAVE_FAILURE' });
      }
    }, 5000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
    // Trigger auto-save when workspace state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.workspaceState, state.sessionState.hintsRevealed, state.sessionState.solutionsRevealed]);

  // ─── Active time tracking ───────────────────────────────────────────────────

  useEffect(() => {
    const recordActivity = () => {
      dispatch({ type: 'RECORD_ACTIVITY', payload: Math.floor(Date.now() / 1000) });
    };

    // Record activity on user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, recordActivity, { passive: true }));

    // Record initial activity
    recordActivity();

    return () => {
      events.forEach((event) => window.removeEventListener(event, recordActivity));
    };
  }, []);

  // ─── Active time getter ─────────────────────────────────────────────────────

  const getActiveTime = useCallback((): number => {
    return calculateActiveTime(state.activityTimestamps);
  }, [state.activityTimestamps]);

  // ─── Context value ──────────────────────────────────────────────────────────

  const value: LLDPracticeContextType = {
    state,
    dispatch,
    getActiveTime,
  };

  return (
    <LLDPracticeContext.Provider value={value}>
      {children}
    </LLDPracticeContext.Provider>
  );
};

// ─── Custom Hook ────────────────────────────────────────────────────────────────

export const useLLDPractice = (): LLDPracticeContextType => {
  const context = useContext(LLDPracticeContext);
  if (!context) {
    throw new Error('useLLDPractice must be used within a LLDPracticeProvider');
  }
  return context;
};

export default LLDPracticeContext;
